<script setup lang="ts">
/* =============================================================
   VESSELS
   =============================================================
   The signed-in company's fleet. Filtered by company id
   explicitly: RLS also lets a berth owner read the ships booked
   at its berths, so "readable" is no longer "mine". The
   dimensions are the reason the page exists — they are what
   search matches berths against.
   ============================================================= */

import { computed, onMounted, ref } from 'vue'
import Alert from '@/components/ui/Alert.vue'
import Badge from '@/components/ui/Badge.vue'
import Button from '@/components/ui/Button.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import FilterInput from '@/components/ui/FilterInput.vue'
import ItemList from '@/components/ui/ItemList.vue'
import ItemRow from '@/components/ui/ItemRow.vue'
import PageHeader from '@/components/ui/PageHeader.vue'
import Panel from '@/components/ui/Panel.vue'
import { routes, to } from '@/config/routes'
import { useAuth } from '@/lib/auth/useAuth'
import { useCountries } from '@/lib/countries'
import { errorMessage } from '@/lib/errors'
import { listVessels } from '@/lib/vessels'
import { useVesselTypes } from '@/lib/vesselTypes'
import { useDocumentTitle } from '@/lib/composables/useDocumentTitle'
import { vesselDimensions } from '@/components/fleet/vesselText'
import type { Vessel } from '@/lib/types'

useDocumentTitle('Vessels')
const { vesselTypeLabel } = useVesselTypes()
const { countryName } = useCountries()

const vessels = ref<Vessel[]>([])
const loading = ref(true)
const error = ref('')
const query = ref('')

const { user } = useAuth()

onMounted(async () => {
  const companyId = user.value?.companyId
  if (!companyId) {
    loading.value = false
    return
  }
  try {
    vessels.value = await listVessels(companyId)
  } catch (e) {
    error.value = errorMessage(e, 'Could not load your vessels.')
  } finally {
    loading.value = false
  }
})

const shown = computed(() => {
  const q = query.value.trim().toLowerCase()
  if (!q) return vessels.value
  return vessels.value.filter((v) =>
    [v.name, vesselTypeLabel(v.type), v.flag_code ? countryName(v.flag_code) : '']
      .join(' ')
      .toLowerCase()
      .includes(q),
  )
})
</script>

<template>
  <PageHeader
    title="Vessels"
    description="The ships you operate. Their size and service needs filter out berths that cannot take them."
  >
    <template #actions>
      <Button icon="plus" :to="routes.vesselNew">Add vessel</Button>
    </template>
  </PageHeader>

  <Alert v-if="error" tone="danger" class="page-alert">{{ error }}</Alert>

  <p v-else-if="loading" class="page-status">Loading…</p>

  <Panel v-else-if="vessels.length === 0">
    <EmptyState
      icon="ship"
      title="No vessels yet"
      description="Add a ship with its length, beam and draft, and search will only show berths it fits."
    >
      <template #action>
        <Button icon="plus" :to="routes.vesselNew">Add vessel</Button>
      </template>
    </EmptyState>
  </Panel>

  <Panel
    v-else
    flush
    title="Fleet"
    :description="`${vessels.length} ${vessels.length === 1 ? 'vessel' : 'vessels'}`"
  >
    <template v-if="vessels.length > 5" #actions>
      <FilterInput v-model="query" label="Filter vessels by name, type or flag" placeholder="Filter vessels" />
    </template>

    <p v-if="shown.length === 0" class="panel-status">No vessel matches “{{ query }}”.</p>
    <ItemList v-else>
      <ItemRow v-for="v in shown" :key="v.id" :to="to.vessel(v.id)">
        <template #title>{{ v.name }}</template>
        <template #aside>
          <Badge>{{ vesselTypeLabel(v.type) }}</Badge>
        </template>
        <span class="mono">{{ vesselDimensions(v) }}</span>
        <span v-if="v.flag_code">Flag: {{ countryName(v.flag_code) }}</span>
      </ItemRow>
    </ItemList>
  </Panel>
</template>

<style scoped>
.mono {
  font-variant-numeric: tabular-nums;
}
</style>
